import { api, getToken } from './client';
import { API_BASE_URL } from '@/lib/config';

// ── Types ──

export interface NavCoverage {
  total: number;
  has_nav: number;
  has_nav_5plus: number;
  zero_nav: number;
  coverage_pct: number;
  fixable: { has_markdown: number; needs_rescrape: number };
  breakdown_by_reason: Record<string, number>;
}

export interface ReprocessProgress {
  type: 'progress' | 'done';
  processed: number;
  total: number;
  updated: number;
  skipped: number;
  errors?: number;
  duration_ms?: number;
}

export interface RescrapeResult {
  queued: number;
  estimated_credits: number;
  estimated_duration_min: number;
  job_id: string;
  task_ids: string[];
}

// ── API ──

export const navHealthApi = {
  getCoverage: async (since?: string) => {
    const params = since ? `?since=${since}` : '';
    const res = await api.get<NavCoverage>(`/scraping/nav-coverage${params}`);
    return res.data!;
  },

  batchReprocess: (body: {
    mode?: string;
    keyword?: string;
    dry_run?: boolean;
    since_hours?: number;
  }): { stream: ReadableStream<ReprocessProgress> | null; abort: () => void } => {
    const controller = new AbortController();
    const token = getToken();
    const url = `${API_BASE_URL}/scraping/batch-reprocess-nav`;

    const streamPromise = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const readable = new ReadableStream<ReprocessProgress>({
      async start(ctrl) {
        try {
          const response = await streamPromise;
          if (!response.body) { ctrl.close(); return; }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6)) as ReprocessProgress;
                  ctrl.enqueue(data);
                } catch { /* skip malformed */ }
              }
            }
          }
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            ctrl.error(err);
          }
        } finally {
          ctrl.close();
        }
      },
    });

    return { stream: readable, abort: () => controller.abort() };
  },

  batchRescrape: async (body: {
    mode?: string;
    domain?: string;
    keyword?: string;
    max_count?: number;
    dry_run?: boolean;
    since_hours?: number;
  }) => {
    const res = await api.post<RescrapeResult>('/scraping/batch-rescrape-nav', body);
    return res.data!;
  },
};
